/**
 * Labels for the guest checkout — stores whose buyers order without an
 * account. Kept beside the locale dictionaries rather than inside them, which
 * are already past the 400-line cap; each dictionary mounts its own language
 * as `guestCheckout`. Words the signed-in checkout already has (field names,
 * "Confirm Order", the coupon box, the order summary) are reused from
 * `checkout.*`, `cart.*` and `common.*` instead of being repeated here.
 */
export const guestCheckoutEn = {
  subtitle: "No account needed. Enter your details and the store will confirm your order.",
  itemsTitle: "Your items",
  detailsTitle: "Your details",
  phonePlaceholder: "01XXXXXXXXX",
  fullAddressLabel: "Full address",
  fullAddressPlaceholder: "Your full address, with district, area and road",
  emailLabel: "Email",
  addEmail: "Add email",
  enterAddressForFees: "Enter your address to see the delivery fee.",
  feeConfirmedByStore: "Fee confirmed by the store",
  feeFixed: "+{amount} fee",
  feePercent: "+{percent}% fee",
  noPaymentMethods: "This store has no payment methods set up yet. Please contact the store to order.",
  paymentReferencePlaceholder: "Transaction or transfer number",
  notesLabel: "Order notes",
  addNote: "Add order note",
  notesPlaceholder: "Anything the store should know about your order",
  estimatedTotal: "Estimated total",
  totalsNote: "The store confirms the final total when your order is placed.",
  paymentMethodLine: "Payment: {method}",
  nameRequired: "Please enter your name.",
  phoneInvalid: "Please enter a valid phone number.",
  phoneInvalidBd: "Please enter a valid Bangladeshi mobile number, like 01712345678.",
  countryRequired: "Please choose your country.",
  divisionRequired: "Please choose your division or state.",
  tooManyOrders: "You have placed several orders in a short time. Please try again a little later.",
  loadFailed: "We couldn't load the checkout options. Please refresh the page.",
  placedTitle: "Thank you! Your order is placed.",
  orderNumber: "Order number: {number}",
  orderTotal: "Total: {amount}",
  placedNote: "The store will contact you to confirm the order.",
};

export type GuestCheckoutDict = typeof guestCheckoutEn;

export const guestCheckoutBn: GuestCheckoutDict = {
  subtitle: "কোনো অ্যাকাউন্ট লাগবে না। আপনার তথ্য দিন, স্টোর আপনার অর্ডার নিশ্চিত করবে।",
  itemsTitle: "আপনার পণ্যসমূহ",
  detailsTitle: "অর্ডারের তথ্য",
  phonePlaceholder: "01XXXXXXXXX",
  fullAddressLabel: "পূর্ণাঙ্গ ঠিকানা",
  fullAddressPlaceholder: "জেলা, থানা ও এলাকার নামসহ পূর্ণ ঠিকানা লিখুন",
  emailLabel: "ইমেইল",
  addEmail: "ইমেইল যোগ করুন",
  enterAddressForFees: "ডেলিভারি চার্জ দেখতে আপনার ঠিকানা দিন।",
  feeConfirmedByStore: "চার্জ স্টোর নিশ্চিত করবে",
  feeFixed: "+{amount} চার্জ",
  feePercent: "+{percent}% চার্জ",
  noPaymentMethods: "এই স্টোরে এখনো কোনো পেমেন্ট পদ্ধতি সেট করা নেই। অর্ডার করতে স্টোরের সাথে যোগাযোগ করুন।",
  paymentReferencePlaceholder: "ট্রানজেকশন বা ট্রান্সফার নম্বর",
  notesLabel: "অর্ডার নোট",
  addNote: "অর্ডার নোট যোগ করুন",
  notesPlaceholder: "আপনার অর্ডার সম্পর্কে স্টোরকে কিছু জানাতে চাইলে লিখুন",
  estimatedTotal: "আনুমানিক মোট",
  totalsNote: "অর্ডার দেওয়ার সময় স্টোর চূড়ান্ত মোট নিশ্চিত করবে।",
  paymentMethodLine: "পেমেন্ট: {method}",
  nameRequired: "অনুগ্রহ করে আপনার নাম লিখুন।",
  phoneInvalid: "অনুগ্রহ করে একটি সঠিক ফোন নম্বর দিন।",
  phoneInvalidBd: "সঠিক মোবাইল নম্বর দিন, যেমন 01712345678।",
  countryRequired: "অনুগ্রহ করে আপনার দেশ নির্বাচন করুন।",
  divisionRequired: "অনুগ্রহ করে আপনার বিভাগ বা রাজ্য নির্বাচন করুন।",
  tooManyOrders: "অল্প সময়ে আপনি কয়েকটি অর্ডার দিয়েছেন। কিছুক্ষণ পরে আবার চেষ্টা করুন।",
  loadFailed: "চেকআউটের অপশনগুলো লোড করা যায়নি। পেজটি রিফ্রেশ করুন।",
  placedTitle: "ধন্যবাদ! আপনার অর্ডার দেওয়া হয়েছে।",
  orderNumber: "অর্ডার নম্বর: {number}",
  orderTotal: "মোট: {amount}",
  placedNote: "অর্ডার নিশ্চিত করতে স্টোর আপনার সাথে যোগাযোগ করবে।",
};

export const guestCheckoutAr: GuestCheckoutDict = {
  subtitle: "لا حاجة إلى حساب. أدخل بياناتك وسيؤكد المتجر طلبك.",
  itemsTitle: "منتجاتك",
  detailsTitle: "بيانات الطلب",
  phonePlaceholder: "XXXXXXXXXX",
  fullAddressLabel: "العنوان الكامل",
  fullAddressPlaceholder: "اكتب عنوانك كاملاً مع المدينة والحي والشارع",
  emailLabel: "البريد الإلكتروني",
  addEmail: "إضافة البريد الإلكتروني",
  enterAddressForFees: "أدخل عنوانك لمعرفة رسوم التوصيل.",
  feeConfirmedByStore: "يؤكد المتجر الرسوم",
  feeFixed: "+{amount} رسوم",
  feePercent: "+{percent}% رسوم",
  noPaymentMethods: "لم يحدد هذا المتجر طرق دفع بعد. يرجى التواصل مع المتجر لإتمام الطلب.",
  paymentReferencePlaceholder: "رقم العملية أو التحويل",
  notesLabel: "ملاحظات الطلب",
  addNote: "إضافة ملاحظة للطلب",
  notesPlaceholder: "أي شيء تود أن يعرفه المتجر عن طلبك",
  estimatedTotal: "الإجمالي التقديري",
  totalsNote: "يؤكد المتجر الإجمالي النهائي عند تقديم طلبك.",
  paymentMethodLine: "الدفع: {method}",
  nameRequired: "يرجى إدخال اسمك.",
  phoneInvalid: "يرجى إدخال رقم هاتف صحيح.",
  phoneInvalidBd: "يرجى إدخال رقم جوال بنغلاديشي صحيح، مثل 01712345678.",
  countryRequired: "يرجى اختيار دولتك.",
  divisionRequired: "يرجى اختيار المنطقة أو الولاية.",
  tooManyOrders: "قدّمت عدة طلبات خلال وقت قصير. يرجى المحاولة مرة أخرى بعد قليل.",
  loadFailed: "تعذّر تحميل خيارات إتمام الطلب. يرجى تحديث الصفحة.",
  placedTitle: "شكراً لك! تم تقديم طلبك.",
  orderNumber: "رقم الطلب: {number}",
  orderTotal: "الإجمالي: {amount}",
  placedNote: "سيتواصل معك المتجر لتأكيد الطلب.",
};
